/* Reviews - TT — our review wall on Loox's data.
   The page arrives with reviews already rendered from Loox's product feed.
   With a public store ID this upgrades to Loox's Storefront API: exact breakdown,
   "would recommend", sort, filter by star, load every review. If the API is
   unreachable, the rendered reviews simply stay — nothing breaks, nothing is guessed. */
(() => {
  if (customElements.get('tt-reviews')) return;

  const API = 'https://storefront-api.loox.io/storefront/v1/store';
  const STAR_PATH = 'M12 2.8l2.7 5.8 6.3.7-4.7 4.3 1.3 6.2L12 16.7l-5.6 3.1 1.3-6.2L3 9.3l6.3-.7z';
  const SORTS = {
    date: { sort: 'date', direction: 'desc' },
    featured: { sort: 'featured', direction: 'desc' },
    high: { sort: 'rating', direction: 'desc' },
    low: { sort: 'rating', direction: 'asc' },
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  const starsSVG = (rating, size) => {
    const wrap = el('span', 'tt-stars');
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', `${rating} out of 5 stars`);
    wrap.style.setProperty('--tt-star-size', `${size}px`);
    const ns = 'http://www.w3.org/2000/svg';
    for (let i = 1; i <= 5; i++) {
      const fill = rating >= i - 0.25 ? 100 : rating >= i - 0.75 ? 50 : 0;
      const svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('class', 'tt-star');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      const id = `tt-star-clip-${fill}`;
      svg.innerHTML =
        `<use class="tt-star__empty" href="#tt-star-shape"/>` +
        (fill ? `<use class="tt-star__fill" clip-path="url(#${id})" href="#tt-star-shape"/>` : '');
      wrap.appendChild(svg);
    }
    return wrap;
  };

  const monthYear = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  class TTReviews extends HTMLElement {
    connectedCallback() {
      this.storeId = (this.dataset.storeId || '').trim();
      this.productId = this.dataset.productId;
      this.perPage = Number(this.dataset.perPage) || 6;
      this.sortKey = this.dataset.sort || 'date';
      this.rating = null;
      this.page = 1;
      this.expanded = false;

      this.grid = this.querySelector('[data-grid]');
      this.moreButton = this.querySelector('[data-more]');
      this.select = this.querySelector('[data-sort-select]');
      this.hist = this.querySelector('[data-hist]');
      this.clearButton = this.querySelector('[data-clear]');
      this.modal = this.querySelector('[data-modal]');
      this.bindModal();

      this.addEventListener('click', (event) => this.onClick(event));
      if (this.select) this.select.addEventListener('change', () => this.onSort());

      this.setupMasonry();

      this.api = Boolean(this.storeId);
      if (this.api) {
        this.loadBottomline();
        this.loadPage(1, true);
      }
    }

    onClick(event) {
      const write = event.target.closest('[data-write]');
      if (write && typeof window.looxWrite === 'function') {
        event.preventDefault();
        window.looxWrite(this.productId);
        return;
      }

      if (this.modal && this.modal.contains(event.target)) return;
      const open = event.target.closest('[data-open]');
      if (open) {
        const card = open.closest('[data-review]');
        if (card) this.openReview(card, Number(open.dataset.open) || 0);
        return;
      }

      const filter = event.target.closest('[data-filter]');
      if (filter) {
        const star = Number(filter.dataset.filter);
        this.setFilter(this.rating === star ? null : star);
        return;
      }
      if (event.target.closest('[data-clear]')) return this.setFilter(null);
      if (event.target.closest('[data-more]')) return this.onMore();
    }

    /* ── masonry: each card spans as many 4px rows as its height needs ── */
    setupMasonry() {
      this.layout = this.layout.bind(this);
      const schedule = () => {
        if (this.layoutFrame) return;
        this.layoutFrame = requestAnimationFrame(() => {
          this.layoutFrame = null;
          this.layout();
        });
      };
      this.scheduleLayout = schedule;
      this.cardObserver = new ResizeObserver(schedule);
      this.cardObserver.observe(this.grid);
      this.observeCards();
      window.addEventListener('resize', schedule);
      this.layout();
    }

    observeCards() {
      this.grid.querySelectorAll('[data-review]').forEach((card) => {
        if (card.dataset.observed) return;
        card.dataset.observed = 'true';
        this.cardObserver.observe(card);
        card.querySelectorAll('img').forEach((img) => img.addEventListener('load', this.scheduleLayout, { once: true }));
      });
    }

    layout() {
      const columns = getComputedStyle(this.grid).gridTemplateColumns.split(' ').filter(Boolean).length;
      const masonry = columns > 1;
      this.grid.classList.toggle('is-masonry', masonry);
      const gap = parseFloat(getComputedStyle(this.grid).getPropertyValue('--tt-rv-gap')) || 16;
      const cards = this.grid.querySelectorAll('[data-review]');
      cards.forEach((card) => {
        if (!masonry || card.hidden) {
          card.style.gridRowEnd = '';
          return;
        }
        card.style.gridRowEnd = '';
        const height = card.getBoundingClientRect().height + gap;
        card.style.gridRowEnd = `span ${Math.ceil(height / 4)}`;
      });
    }

    reveal(cards) {
      cards.forEach((card) => {
        card.classList.remove('is-new');
        void card.offsetWidth;
        card.classList.add('is-new');
      });
    }

    /* ── API ── */
    async fetchJSON(path, params = {}) {
      const url = new URL(`${API}/${encodeURIComponent(this.storeId)}${path}`);
      Object.entries(params).forEach(([k, v]) => v != null && url.searchParams.set(k, v));
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Loox API ${response.status}`);
      return response.json();
    }

    async loadBottomline() {
      try {
        const data = await this.fetchJSON(`/product-reviews/bottomline/${this.productId}`);
        this.renderBottomline(data);
      } catch (error) {
        console.warn('[Reviews - TT] breakdown unavailable:', error.message);
      }
    }

    async loadPage(page, replace) {
      const { sort, direction } = SORTS[this.sortKey] || SORTS.date;
      this.grid.classList.add('is-loading');
      try {
        const data = await this.fetchJSON('/product-reviews', {
          product_id: this.productId,
          sort,
          direction,
          rating: this.rating,
          page,
          limit: this.perPage,
          total: page > 1 ? this.total : undefined,
        });
        this.page = page;
        this.total = data.pagination?.total;
        this.hasMore = Boolean(data.pagination?.hasMore);
        this.renderReviews(data.reviews || [], replace);
      } catch (error) {
        console.warn('[Reviews - TT] Loox API unavailable, keeping the saved reviews:', error.message);
        this.api = false;
        this.syncMoreButton();
      } finally {
        this.grid.classList.remove('is-loading');
      }
    }

    /* ── actions ── */
    onSort() {
      this.sortKey = this.select.value;
      if (this.api) return this.loadPage(1, true);
      this.sortLocal();
    }

    setFilter(star) {
      this.rating = star;
      if (this.hist) {
        this.hist.classList.toggle('is-filtering', star != null);
        this.hist.querySelectorAll('[data-filter]').forEach((row) => {
          row.setAttribute('aria-pressed', Number(row.dataset.filter) === star ? 'true' : 'false');
        });
      }
      if (this.clearButton) {
        this.clearButton.hidden = star == null;
        const label = this.clearButton.querySelector('[data-clear-label]');
        if (label) label.textContent = star != null ? `${star}★ only` : '';
      }
      this.expanded = false;
      if (this.api) this.loadPage(1, true);
    }

    onMore() {
      if (this.api) {
        if (this.hasMore) return this.loadPage(this.page + 1, false);
        /* everything is showing: collapse back to the first page */
        this.loadPage(1, true);
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const cards = Array.from(this.grid.querySelectorAll('[data-review]'));
      const hidden = cards.filter((card) => card.hidden);
      if (hidden.length) {
        const shown = hidden.slice(0, this.perPage);
        shown.forEach((card) => (card.hidden = false));
        this.reveal(shown);
      } else {
        cards.forEach((card, i) => (card.hidden = i >= this.perPage));
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      this.syncMoreButton();
      this.layout();
    }

    sortLocal() {
      const cards = Array.from(this.grid.querySelectorAll('[data-review]'));
      const by = {
        date: (a, b) => b.dataset.date.localeCompare(a.dataset.date),
        featured: (a, b) => Number(a.dataset.featured || 0) - Number(b.dataset.featured || 0),
        high: (a, b) => b.dataset.rating - a.dataset.rating || b.dataset.date.localeCompare(a.dataset.date),
        low: (a, b) => a.dataset.rating - b.dataset.rating || b.dataset.date.localeCompare(a.dataset.date),
      };
      const sorted = cards.sort(by[this.sortKey] || by.date);
      sorted.forEach((card, i) => {
        card.hidden = i >= this.perPage;
        this.grid.appendChild(card);
      });
      this.reveal(sorted.slice(0, this.perPage));
      this.syncMoreButton();
      this.layout();
    }

    syncMoreButton() {
      if (!this.moreButton) return;
      if (this.api) {
        const showingAll = !this.hasMore;
        const shown = this.grid.querySelectorAll('[data-review]').length;
        this.moreButton.hidden = showingAll && shown <= this.perPage;
        this.moreButton.textContent = showingAll ? this.dataset.fewerLabel : this.dataset.moreLabel;
        return;
      }
      const cards = Array.from(this.grid.querySelectorAll('[data-review]'));
      const anyHidden = cards.some((card) => card.hidden);
      this.moreButton.hidden = cards.length <= this.perPage;
      this.moreButton.textContent = anyHidden ? this.dataset.moreLabel : this.dataset.fewerLabel;
    }

    /* ── rendering ── */
    renderBottomline(data) {
      const total = Number(data.totalReviews) || 0;
      const dist = data.ratingDistribution || {};
      if (!total) return;

      this.querySelectorAll('[data-total]').forEach((node) => (node.textContent = total));
      const avgNode = this.querySelector('[data-avg]');
      if (avgNode) avgNode.textContent = Number(data.averageRating).toFixed(1);
      const avgStars = this.querySelector('[data-avg-stars]');
      if (avgStars) avgStars.replaceChildren(starsSVG(Number(data.averageRating), 20));

      if (this.hist) {
        for (let star = 1; star <= 5; star++) {
          const n = Number(dist[star]) || 0;
          const fill = this.hist.querySelector(`[data-fill="${star}"]`);
          const count = this.hist.querySelector(`[data-count="${star}"]`);
          if (count) count.textContent = n;
          if (fill) requestAnimationFrame(() => (fill.style.width = `${(n / total) * 100}%`));
        }
        this.hist.hidden = false;
        this.querySelector('.tt-rv__summary')?.classList.add('has-breakdown');
      }

      /* "would recommend": the share of every published review rated 4 or 5 stars */
      const recommend = this.querySelector('[data-recommend]');
      if (recommend) {
        const share = Math.round((((Number(dist[4]) || 0) + (Number(dist[5]) || 0)) / total) * 100);
        recommend.querySelector('[data-recommend-value]').textContent = `${share}%`;
        recommend.hidden = false;
      }
    }

    renderReviews(reviews, replace) {
      if (replace) this.grid.replaceChildren();
      reviews.forEach((review) => this.grid.appendChild(this.card(review, !replace)));
      if (replace && !reviews.length) {
        this.grid.appendChild(el('li', 'tt-rv__none', 'No reviews with that rating yet.'));
      }
      this.observeCards();
      this.syncMoreButton();
      this.layout();
    }

    /* one media shape for both sources: Loox's product feed ({t, src, w, h}) and its API ({type, url, thumbnailUrl}) */
    static normalizeMedia(list) {
      return (list || []).map((m) => {
        if (m.type) {
          return {
            type: m.type,
            full: m.url,
            thumb: m.type === 'video' ? m.thumbnailUrl : m.url.replace('_orig.', '_mid.'),
            w: m.w,
            h: m.h,
          };
        }
        const src = (m.src || '').startsWith('//') ? `https:${m.src}` : m.src;
        return {
          type: m.t === 'video' ? 'video' : 'photo',
          full: m.t === 'video' ? null : src.replace('_mid.', '_orig.'),
          thumb: src,
          w: m.w,
          h: m.h,
        };
      });
    }

    card(review, animate) {
      const media = TTReviews.normalizeMedia(review.media);
      const li = el('li', `tt-rv__card${media.length ? ' has-media' : ''}${animate ? ' is-new' : ''}`);
      li.dataset.review = '';
      li.dataset.id = review.id;
      li.dataset.rating = review.rating;
      li.dataset.date = review.date || review.createdAt || '';
      li.dataset.name = review.reviewer?.name || 'A customer';
      li.dataset.verified = review.verified ? 'true' : 'false';
      li.dataset.normalized = JSON.stringify(media);
      if (review.reply?.body) li.dataset.reply = review.reply.body;

      if (media.length) {
        const visual = el('button', 'tt-rv__visual');
        visual.type = 'button';
        visual.dataset.open = '0';
        visual.setAttribute('aria-label', `Open review from ${li.dataset.name}`);
        const img = el('img');
        img.src = media[0].thumb;
        img.alt = `Customer ${media[0].type} from ${li.dataset.name}`;
        img.loading = 'lazy';
        if (media[0].w && media[0].h) img.style.aspectRatio = `${media[0].w} / ${media[0].h}`;
        visual.appendChild(img);
        if (media[0].type === 'video') visual.appendChild(el('span', 'tt-rv__play'));
        if (media.length > 1) {
          const count = el('span', 'tt-rv__count');
          count.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="7" y="3" width="14" height="14" rx="3" stroke="currentColor" stroke-width="2.2"/><path d="M3 8v10a3 3 0 0 0 3 3h10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
          count.appendChild(document.createTextNode(`+${media.length - 1}`));
          visual.appendChild(count);
        }
        li.appendChild(visual);
      }

      const content = el('div', 'tt-rv__content');
      const top = el('div', 'tt-rv__card-top');
      top.appendChild(starsSVG(Number(review.rating), 16));
      const time = el('time', 'tt-rv__date', monthYear(li.dataset.date));
      time.dateTime = li.dataset.date;
      top.appendChild(time);
      content.appendChild(top);

      if (review.body) content.appendChild(el('p', 'tt-rv__body', review.body));

      if (review.reply?.body) {
        const reply = el('p', 'tt-rv__reply');
        reply.appendChild(el('b', null, this.dataset.replyLabel || 'Reply'));
        reply.appendChild(document.createTextNode(review.reply.body));
        content.appendChild(reply);
      }

      const foot = el('div', 'tt-rv__card-foot');
      foot.appendChild(el('span', 'tt-rv__name', li.dataset.name));
      if (review.verified) foot.appendChild(el('span', 'tt-rv__verified', `· ${this.dataset.verifiedLabel}`));
      content.appendChild(foot);
      li.appendChild(content);
      return li;
    }

    /* ── the review viewer: media on one side, the whole review on the other ── */
    bindModal() {
      if (!this.modal) return;
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal || event.target.closest('[data-modal-close]')) return this.closeReview();
        if (event.target.closest('[data-modal-prev]')) return this.showMedia(this.mediaIndex - 1);
        if (event.target.closest('[data-modal-next]')) return this.showMedia(this.mediaIndex + 1);
        const thumb = event.target.closest('[data-thumb]');
        if (thumb) this.showMedia(Number(thumb.dataset.thumb));
      });
      this.modal.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.showMedia(this.mediaIndex - 1);
        if (event.key === 'ArrowRight') this.showMedia(this.mediaIndex + 1);
      });
      this.modal.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        const stage = this.modal.querySelector('[data-modal-stage]');
        if (stage) stage.replaceChildren();
      });
    }

    async openReview(card, index) {
      if (!this.modal) return;
      const q = (sel) => this.modal.querySelector(sel);
      this.modalMedia = card.dataset.normalized
        ? JSON.parse(card.dataset.normalized)
        : TTReviews.normalizeMedia(JSON.parse(card.dataset.media || '[]'));

      q('[data-modal-name]').textContent = card.dataset.name || '';
      q('[data-modal-verified]').hidden = card.dataset.verified !== 'true';
      q('[data-modal-stars]').replaceChildren(starsSVG(Number(card.dataset.rating), 18));
      const date = q('[data-modal-date]');
      date.textContent = monthYear(card.dataset.date);
      date.dateTime = card.dataset.date;
      const bodyNode = card.querySelector('.tt-rv__body');
      q('[data-modal-body]').textContent = bodyNode ? bodyNode.innerText : '';
      const reply = q('[data-modal-reply]');
      reply.replaceChildren();
      if (card.dataset.reply) {
        reply.appendChild(el('b', null, this.dataset.replyLabel || 'Reply'));
        reply.appendChild(document.createTextNode(card.dataset.reply));
      }
      reply.hidden = !card.dataset.reply;

      this.modal.classList.toggle('no-media', !this.modalMedia.length);
      const thumbs = q('[data-modal-thumbs]');
      thumbs.replaceChildren();
      if (this.modalMedia.length > 1) {
        this.modalMedia.forEach((m, k) => {
          const b = el('button', 'tt-rv__modal-thumb');
          b.type = 'button';
          b.dataset.thumb = k;
          b.setAttribute('aria-label', `Show ${m.type} ${k + 1}`);
          const img = el('img');
          img.src = m.thumb;
          img.alt = '';
          b.appendChild(img);
          if (m.type === 'video') b.appendChild(el('span', 'tt-rv__play tt-rv__play--small'));
          thumbs.appendChild(b);
        });
      }

      this.modalCard = card;
      this.showMedia(index);
      if (!this.modal.open) {
        this.modal.showModal();
        document.documentElement.style.overflow = 'hidden';
      }
      const info = this.modal.querySelector('.tt-rv__modal-info');
      if (info) info.scrollTop = 0;
    }

    closeReview() {
      if (this.modal?.open) this.modal.close();
    }

    async showMedia(i) {
      const list = this.modalMedia || [];
      if (!list.length) return;
      const index = (i + list.length) % list.length;
      this.mediaIndex = index;
      const q = (sel) => this.modal.querySelector(sel);
      const stage = q('[data-modal-stage]');
      const m = list[index];

      q('[data-modal-prev]').hidden = list.length < 2;
      q('[data-modal-next]').hidden = list.length < 2;
      q('[data-modal-thumbs]').querySelectorAll('[data-thumb]').forEach((t, k) => {
        t.setAttribute('aria-current', k === index ? 'true' : 'false');
      });

      stage.replaceChildren();
      if (m.type === 'video') {
        let url = m.full;
        /* the product feed only carries the poster — ask Loox for the stream */
        if (!url && this.storeId && this.modalCard?.dataset.id) {
          const poster = el('img');
          poster.src = m.thumb;
          poster.alt = '';
          stage.appendChild(poster);
          try {
            const review = await this.fetchJSON(`/product-reviews/${this.modalCard.dataset.id}`);
            const fresh = TTReviews.normalizeMedia((review.review || review).media);
            url = fresh[index]?.full;
            if (url) list[index].full = url;
          } catch (error) {
            console.warn('[Reviews - TT] video unavailable:', error.message);
          }
          if (this.mediaIndex !== index) return;
          stage.replaceChildren();
        }
        if (url) {
          const frame = el('iframe');
          frame.src = `${url}${url.includes('?') ? '&' : '?'}autoplay=true`;
          frame.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
          frame.allowFullscreen = true;
          frame.title = 'Customer video review';
          stage.appendChild(frame);
        } else {
          const poster = el('img');
          poster.src = m.thumb;
          poster.alt = '';
          stage.appendChild(poster);
        }
        return;
      }

      const img = el('img');
      img.src = m.full || m.thumb;
      img.alt = `Customer photo from ${this.modalCard?.dataset.name || 'a customer'}`;
      stage.appendChild(img);
    }
  }

  customElements.define('tt-reviews', TTReviews);

  /* Rating - TT in the product section: glide down to the reviews, clearing the sticky header */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-tt-rating-link]');
    if (!link) return;
    const id = decodeURIComponent((link.getAttribute('href') || '').replace('#', ''));
    const target = id && document.getElementById(id);
    if (!target) return;
    event.preventDefault();

    /* whatever stays pinned to the top once we arrive: sticky/fixed header-group sections */
    const offset = Array.from(document.querySelectorAll('.shopify-section-group-header-group')).reduce((max, el) => {
      const position = getComputedStyle(el).position;
      if (position !== 'sticky' && position !== 'fixed') return max;
      return Math.max(max, el.getBoundingClientRect().height);
    }, 0);
    const top = target.getBoundingClientRect().top + window.scrollY - offset + 1;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
    history.replaceState(null, '', `#${id}`);
  });
})();
